import { FlexyHeaderForm, useModal } from '@europrocurement/flexy-components';
import { Box, Button, Grid, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { FormaliteDocument } from '@europrocurement/l2d-domain';
import { type TypeDeDocumentJsonldTypedocumentRead } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { CheckboxListItem } from '@europrocurement/l2d-modules/modules/SelectFormality';

export type DocumentTypeItem = {
    id: number;
    label: string;
    checked: boolean;
    type: number;
};

type DocumentTypeFormProps = {
    title: string;
    type: number;
    itemsSelected: number[];
    handleSelectItems: (selectedItems: unknown) => void;
    singleChoice?: boolean;
    getTypeDocuments: () => TypeDeDocumentJsonldTypedocumentRead[];
};

export type DocTypeItem = {
    id: number;
    label: string;
};

const DocumentTypeForm: React.FunctionComponent<DocumentTypeFormProps> = function ({
    title,
    type,
    itemsSelected,
    handleSelectItems,
    singleChoice = false,
    getTypeDocuments,
}) {
    const { modalActions } = useModal();
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedItems, setSelectedItems] = useState<DocTypeItem[]>([]);

    const documentsItems = React.useMemo(
        () =>
            getTypeDocuments()
                .map((item: FormaliteDocument) => ({
                    id: item.id,
                    label: item.libelle,
                    checked: selectedItems.some((selectedItem) => selectedItem.id === item.id),
                    type: item.type,
                }))
                .filter(
                    (item: DocumentTypeItem) =>
                        type === item.type &&
                        (!searchTerm ||
                            item.label.toLowerCase().includes(searchTerm.toLowerCase())),
                )
                .sort((a: DocumentTypeItem, b: DocumentTypeItem) => {
                    if (a.label.toLowerCase() < b.label.toLowerCase()) {
                        return -1;
                    }

                    if (a.label.toLowerCase() > b.label.toLowerCase()) {
                        return 1;
                    }

                    return 0;
                }) as DocumentTypeItem[],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [searchTerm, selectedItems, type],
    );

    useEffect(() => {
        const preselected = getTypeDocuments()
            .filter((doc) => itemsSelected.includes(doc.id as number))
            .map((doc) => ({
                id: doc.id as number,
                label: doc.libelle as string,
            }));

        setSelectedItems(preselected);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemsSelected, type]);

    const onCheckItem = (item: DocTypeItem) => {
        if (!selectedItems.some((selectedItem) => selectedItem.id === item.id)) {
            if (singleChoice) {
                const document = documentsItems.find((typeDocType) => typeDocType.id === item.id);
                handleSelectItems(document);
                modalActions.close();
            } else {
                setSelectedItems([...selectedItems, item]);
            }
        }
    };
    const onUncheckItem = (item: DocTypeItem) => {
        if (selectedItems.some((selectedItem) => selectedItem.id === item.id)) {
            setSelectedItems(selectedItems.filter((selectedItem) => selectedItem.id !== item.id));
        }
    };

    return (
        <Box sx={{ width: '500px' }}>
            <Box sx={{ display: 'grid', gap: '10px' }}>
                <FlexyHeaderForm
                    outlined
                    label={title}
                    sx={{ marginTop: '-15px' }}
                />
                <TextField
                    fullWidth
                    placeholder="Filtrer la liste des documents"
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                    }}
                />
                {documentsItems.length < 1 ? (
                    <Box>Type de document non trouvé</Box>
                ) : (
                    <Box sx={{ maxHeight: '45vh', overflowY: 'scroll' }}>
                        <Grid
                            container
                            gap="10px 0"
                        >
                            {documentsItems.map((item: DocumentTypeItem) => (
                                <Grid
                                    key={item.id}
                                    item
                                    style={{ width: '100%' }}
                                >
                                    <CheckboxListItem
                                        label={item.label}
                                        checked={item.checked}
                                        onUpdate={(isChecked: boolean) => {
                                            if (isChecked) {
                                                onCheckItem({ id: item.id, label: item.label });
                                            } else {
                                                onUncheckItem({ id: item.id, label: item.label });
                                            }
                                        }}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}
                {!singleChoice && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            paddingTop: '26px',
                        }}
                    >
                        <Button
                            key="document-modal-cancel-btn"
                            color="primary"
                            onClick={() => modalActions.close()}
                            sx={{ padding: '16px 24px', fontSize: '16px' }}
                        >
                            Fermer
                        </Button>
                        {documentsItems.length > 0 && (
                            <Button
                                key="document-modal-submit-btn"
                                color="primary"
                                variant="contained"
                                onClick={() => handleSelectItems(selectedItems)}
                                sx={{ padding: '16px 24px', fontSize: '16px' }}
                            >
                                Confirmer
                            </Button>
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default DocumentTypeForm;
